import { type StateCommand } from "@codemirror/state";
import { useMutation } from "@tanstack/react-query";
import {
  Bold,
  Code,
  Code2,
  Download,
  Eye,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link2,
  Loader2,
  Maximize2,
  Minimize2,
  PenLine,
} from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { ErrorState } from "@/components/feedback";
import { CodeMirrorEditor, type CodeMirrorEditorHandle } from "@/components/reader/cm/CodeMirrorEditor";
import { insertImage, insertLink, insertTable, wrapRange } from "@/components/reader/cm/keymap";
import { parseTable } from "@/components/reader/cm/live-preview";
import { isDelimiterRow } from "@/components/reader/cm/table-edit";
import { PreviewTable } from "@/components/reader/PreviewTable";
import { TableSizePicker } from "@/components/reader/TableSizePicker";
import { Markdown } from "@/components/shared/Markdown";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Tip,
} from "@/components/ui/overlays";
import { ScrollArea } from "@/components/ui/primitives";
import { DocTitleBar } from "@/components/workspace/content/DocTitleBar";
import { keys, useNode } from "@/hooks/queries";
import { api, flushNodeText, GraphMindApiError, queryClient, type NodeOut } from "@/lib/api";
import { ensureFreshToken, forceRefresh } from "@/lib/auth-token";
import { downloadArtifact, sanitizeFilename, saveBlobAsFile } from "@/lib/download";
import { notifyError } from "@/lib/errors";
import { ATLAS_MASTER_LABEL } from "@/lib/create-note";
import { cn } from "@/lib/utils";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

/** Upload an image to embed IN the note body and resolve to a path/URL usable
 * as `![](path)` — the "smart file structure" affordance behind the toolbar's
 * Insert Image button and paste/drop-onto-the-editor.
 *
 * Posts to `POST /api/attachments` (routes.py::upload_attachment), which
 * exists and works. That route is deliberately NOT scoped to a document or a
 * node the way `/api/documents/{id}/assets` and `/api/nodes/{id}/thumbnail`
 * are, because an image can be pasted into a brand-new note that has never
 * been saved: it stores the bytes in the blob store under a per-user
 * `attachments/{user_id}/` prefix and returns `path`/`url`, both the same
 * already-servable `/api/attachments/{name}` value, usable as `![](path)`
 * with no further resolution. Auth rides the id-token cookie, with one
 * refresh and retry on a 401 or 403. A genuine failure is surfaced as an
 * honest error; it never fabricates a fake image. */
async function uploadNoteAttachment(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const send = async () => {
    await ensureFreshToken();
    // B RULE-006 exception, removed by remediation item 4.1d.
    // eslint-disable-next-line no-restricted-globals
    return fetch(`/api/attachments`, { method: "POST", body: form });
  };
  let resp = await send();
  if ((resp.status === 401 || resp.status === 403) && (await forceRefresh())) {
    resp = await send();
  }
  if (!resp.ok) {
    throw new GraphMindApiError(resp.status, await resp.text().catch(() => ""));
  }
  const data = (await resp.json()) as { path?: string; url?: string };
  const path = data.path ?? data.url;
  if (!path) throw new Error("Attachment upload response was missing a path");
  return path;
}

/** The text of the note body's first level-1 ATX heading (`# …`), or null if it
 * has none. Fence-aware so a `#` inside a code block is never mistaken for the
 * title. This is the note's "big H1" — the one value the thin title bar and the
 * filename reconcile against (redesign point 4). */
export function firstH1(md: string): string | null {
  let inFence = false;
  let fence = "";
  for (const line of md.split("\n")) {
    const f = line.match(/^\s*(```+|~~~+)/);
    if (f) {
      const tick = (f[1] ?? "").charAt(0);
      if (!inFence) {
        inFence = true;
        fence = tick;
      } else if (line.trim().startsWith(fence)) {
        inFence = false;
      }
      continue;
    }
    if (inFence) continue;
    const h = line.match(/^#\s+(.+?)\s*$/);
    if (h) return (h[1] ?? "").trim() || null;
  }
  return null;
}

/** Rewrite the note body's first `# ` heading to `# ${title}`. Returns null when
 * there is NO H1 to stamp — deliberately conservative: renaming a note never
 * INJECTS a heading into a body that has none (that would surprise; the file is
 * still renamed either way), it only keeps an existing title heading in step. */
export function stampH1(md: string, title: string): string | null {
  const lines = md.split("\n");
  let inFence = false;
  let fence = "";
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const f = line.match(/^\s*(```+|~~~+)/);
    if (f) {
      const tick = (f[1] ?? "").charAt(0);
      if (!inFence) {
        inFence = true;
        fence = tick;
      } else if (line.trim().startsWith(fence)) {
        inFence = false;
      }
      continue;
    }
    if (inFence) continue;
    if (/^#\s+/.test(line)) {
      lines[i] = `# ${title}`;
      return lines.join("\n");
    }
  }
  return null;
}

type Mode = "live" | "source" | "preview";

/** Debounce before the body's first `# ` heading is reconciled onto the vault
 * filename — longer than AUTOSAVE_MS so the body saves first and a rapidly-typed
 * heading renames the file once (at the pause), not per keystroke. */
const H1_RENAME_MS = 1200;

/** Debounce before an edit is autosaved — long enough not to save mid-word,
 * short enough that little is at risk if the page is closed before it fires
 * (the pagehide/unmount flush is the backstop for that window). */
const AUTOSAVE_MS = 800;

/** Split markdown into top-level blocks for CHUNKED compilation: each block is
 * rendered by its own memoized <Markdown>, so editing one paragraph only
 * recompiles that paragraph (large notes stay responsive). Fenced code blocks
 * (``` or ~~~) are kept intact — their internal blank lines never split them. */
export function splitBlocks(md: string): string[] {
  return splitBlockRanges(md).map((b) => b.text);
}

/** The same split, but keeping each block's SOURCE RANGE. Preview needs the
 * offsets so an edit made in a rendered block (see `PreviewTable`) can be
 * written back to exactly those bytes — rejoining the block texts would
 * normalise the note's blank lines instead. */
export function splitBlockRanges(md: string): { text: string; from: number; to: number }[] {
  const lines = md.split("\n");
  const lineStart: number[] = [];
  let offset = 0;
  for (const line of lines) {
    lineStart.push(offset);
    offset += line.length + 1;
  }

  const blocks: { text: string; from: number; to: number }[] = [];
  const push = (fromLine: number, toLine: number) => {
    if (toLine < fromLine) return;
    const text = lines.slice(fromLine, toLine + 1).join("\n");
    if (!text.trim()) return;
    blocks.push({
      text,
      from: lineStart[fromLine] ?? 0,
      to: (lineStart[toLine] ?? 0) + (lines[toLine]?.length ?? 0),
    });
  };

  let open = -1; // first line of the block being accumulated, or -1
  let inFence = false;
  let fence = "";
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const f = line.match(/^\s*(```+|~~~+)/);
    if (f) {
      const tick = (f[1] ?? "").charAt(0);
      if (!inFence) {
        inFence = true;
        fence = tick;
      } else if (line.trim().startsWith(fence)) {
        inFence = false;
      }
      if (open < 0) open = i;
      continue;
    }
    if (inFence) {
      if (open < 0) open = i;
      continue;
    }
    if (!line.trim()) {
      push(open, i - 1);
      open = -1;
      continue;
    }
    // A TABLE always gets a block of its own, even with no blank line above it.
    // GFM lets a table interrupt a paragraph, and the toolbar's picker inserts
    // one directly under whatever you were writing — so a table very often
    // shares a block with the prose above it. Preview swaps a table block for
    // the editable grid, and that swap only fires when the block IS the table;
    // sharing meant a freshly inserted table silently stayed read-only.
    if (line.includes("|") && isDelimiterRow(lines[i + 1] ?? "")) {
      push(open, i - 1);
      open = -1;
      let end = i + 1;
      while (end + 1 < lines.length && (lines[end + 1] ?? "").includes("|") && (lines[end + 1] ?? "").trim()) {
        end++;
      }
      push(i, end);
      i = end;
      continue;
    }
    if (open < 0) open = i;
  }
  push(open, lines.length - 1);
  return blocks;
}

const Block = memo(function Block({ text }: { text: string }) {
  return <Markdown text={text} />;
});

/** Compile the note in chunks so unchanged blocks aren't re-rendered.
 *
 * A block that is a TABLE is handed to `PreviewTable` instead of the markdown
 * renderer: in Preview there is no requirement to look like the source, so a
 * table becomes a grid of editable boxes and its markdown is regenerated from
 * them. Every other block renders exactly as before. */
function ChunkedPreview({
  text,
  onEditRange,
}: {
  text: string;
  /** Replace `[from, to)` of the note with `next`. */
  onEditRange: (from: number, to: number, next: string) => void;
}) {
  const blocks = useMemo(() => splitBlockRanges(text), [text]);
  if (!blocks.length) {
    return <p className="font-display italic text-muted-foreground">Nothing to preview yet.</p>;
  }
  return (
    <div className="space-y-1">
      {blocks.map((b, i) =>
        parseTable(b.text) ? (
          <PreviewTable
            key={`t${i}:${b.from}`}
            source={b.text}
            onChange={(next) => onEditRange(b.from, b.to, next)}
          />
        ) : (
          <Block key={`${i}:${b.text.slice(0, 24)}`} text={b.text} />
        ),
      )}
    </div>
  );
}

/** Rich markdown editor for a note. A single CodeMirror surface compiles the
 * markup inline as you type (Obsidian "Live Preview"); a toggle switches between
 * Live (decorations on), Source (raw markdown), and a read-only Preview. Editing
 * shortcuts live in the CodeMirror keymap: Cmd/Ctrl+B bold, +I italic, +E inline
 * code, +`=`/Shift+H highlight, +K link, +S save. */
export function MarkdownEditor({
  nodeId,
  visible,
  panelId,
}: {
  nodeId: string;
  visible: boolean;
  /** The OWNING workspace panel's id, threaded from PanelContent. The 404
   * self-close below closes exactly this panel — a markdown note rendered
   * inside a legacy `doc:` panel has a panel id of `doc:${id}`, so the
   * computed `markdown:${id}` fallback would miss it and strand a re-404ing
   * ghost. Absent (compact/legacy mounts) ⇒ fall back to the computed id. */
  panelId?: string;
}) {
  const node = useNode(nodeId);
  const [draft, setDraft] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("live");
  // Fullscreen ("borderless text sheet"): the app chrome hides; markdown keeps
  // its thin title bar + this toolbar (they're the writing surface itself).
  const fullscreen = useShell((s) => s.fullscreen);
  const toggleFullscreen = useShell((s) => s.toggleFullscreen);
  const editorRef = useRef<CodeMirrorEditorHandle>(null);
  // The body's first `# ` heading as we last reconciled it. Seeded from the note
  // on load so an EXISTING mismatch (a note whose filename already differs from
  // its H1) is left ALONE — we only reconcile when the H1 line the user is
  // editing actually CHANGES, never on open. See the reconcile effect below.
  const lastH1Ref = useRef<string | null>(null);

  // Seed the draft from the loaded note (once).
  useEffect(() => {
    if (node.data && draft === null) {
      setDraft(node.data.text ?? "");
      lastH1Ref.current = firstH1(node.data.text ?? "");
    }
  }, [node.data, draft]);

  // Same self-heal as ReaderTab: a note deleted since this tab was opened
  // 404s here forever otherwise (the workspace layout, `graphmind.workspace.
  // layout.v1`, persists tabs across reloads) — close the stale tab instead of
  // leaving a dead "could not be loaded" ghost. Close the OWNING panel when its
  // id was threaded in (works for a note mounted in either a `markdown:` or a
  // legacy `doc:` panel); `useTabs.close` no-ops if it's already gone.
  useEffect(() => {
    if (node.error instanceof GraphMindApiError && node.error.status === 404) {
      toast.error("That note was deleted", { description: "Closing this tab." });
      useTabs.getState().close(panelId ?? `markdown:${nodeId}`);
    }
  }, [node.error, nodeId, panelId]);

  // Insert-image-in-body: the toolbar button and paste/drop-onto-the-editor
  // both funnel into here. Uploads first, then inserts `![](path)` at the
  // editor's current cursor via the same imperative handle the formatting
  // toolbar uses — see `insertImage` in cm/keymap.ts.
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [insertingImage, setInsertingImage] = useState(false);
  const insertImageFromFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("That file isn't an image");
      return;
    }
    setInsertingImage(true);
    try {
      const path = await uploadNoteAttachment(file);
      editorRef.current?.runCommand(insertImage(path));
    } catch (e) {
      toast.error("Could not insert image", { description: String(e) });
    } finally {
      setInsertingImage(false);
    }
  };

  const save = useMutation({
    mutationFn: (text: string) => api.request("PATCH", `/api/nodes/${nodeId}`, { text }),
    onSuccess: () => {
      // No success toast: autosave fires often, so the Saving…/Saved button
      // state is the status affordance. Errors still surface loudly.
      void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
      void queryClient.invalidateQueries({ queryKey: keys.vault });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
    onError: (e) => toast.error("Could not save", { description: String(e) }),
  });

  const rename = useMutation({
    mutationFn: (label: string) => api.request<NodeOut>("PATCH", `/api/nodes/${nodeId}`, { label }),
    onSuccess: (updated) => {
      // Patch the cached node with the server-confirmed label immediately —
      // the title reflects the rename the instant it lands, not after the
      // invalidated queries below round-trip a refetch.
      queryClient.setQueryData<NodeOut>(keys.node(nodeId), (prev) =>
        prev ? { ...prev, label: updated.label } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
      void queryClient.invalidateQueries({ queryKey: keys.vault });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
    onError: (e) => toast.error("Could not rename", { description: String(e) }),
  });

  // The thin title bar edits the note's FILENAME (`label`). Renaming also stamps
  // the body's first `# ` heading so the visible big title stays in step (title
  // = filename = H1, redesign point 4). Stamping only rewrites an EXISTING H1 —
  // it never injects a heading into a body that has none. Updating the draft
  // rides the normal autosave to persist the body; it can't loop, because the
  // reconcile effect below then sees H1 == label.
  const renameNote = (next: string) => {
    rename.mutate(next);
    if (draft !== null) {
      const stamped = stampH1(draft, next);
      if (stamped !== null && stamped !== draft) {
        // The stamp sets the body's H1 to `next` too — record it so the reconcile
        // effect below doesn't treat it as a user H1 edit and rename all over again.
        lastH1Ref.current = next;
        setDraft(stamped);
      }
    }
  };

  // Run a CodeMirror formatting command (bold/italic/link/…) from a toolbar
  // click — a thin pass-through to the editor's imperative handle.
  const runFormat = (cmd: StateCommand) => editorRef.current?.runCommand(cmd);

  const dirty = draft !== null && draft !== (node.data?.text ?? "");

  // Stable handles: react-query's `save` object is a new reference each render,
  // so depending on it would re-fire the autosave effect on every render.
  const { mutate: runSave, mutateAsync: saveAsync, isPending: saving } = save;

  // Autosave: persist the draft a short beat after the user stops typing, so a
  // refresh, navigation, or tab close never silently drops work. `saving` is a
  // dep so that once an in-flight save settles, edits made during it get picked
  // up by a fresh timer.
  useEffect(() => {
    if (draft === null || !dirty || saving) return;
    const t = window.setTimeout(() => runSave(draft), AUTOSAVE_MS);
    return () => window.clearTimeout(t);
  }, [draft, dirty, saving, runSave]);

  // H1 → filename reconcile (the other half of redesign point 4): when the user
  // EDITS the body's first `# ` heading, rename the file to match. Gated on the
  // H1 having actually CHANGED (vs `lastH1Ref`) so it never fires on open for a
  // note whose filename already differs from its H1, and never on an edit that
  // leaves the H1 untouched. Debounced (> AUTOSAVE_MS) so a rapidly-typed
  // heading renames once, at the pause. Cannot loop: the rename only PATCHes the
  // label; the body already holds the H1, so a later pass sees no further change
  // (and the thin-bar → H1 stamp records `lastH1Ref` up front). Refs keep the
  // timer reading the freshest label / rename state without re-subscribing.
  const label = node.data?.label;
  // The Atlas Master File is found again BY LABEL (the rail button resolves it),
  // so its name is not the user's to change — renaming it would orphan the
  // button and quietly create a second one on the next press. The title bar goes
  // read-only and the H1 reconcile below is skipped, which together also stop an
  // edit to the body's `# ` heading from renaming it out from under itself.
  const locked = label === ATLAS_MASTER_LABEL;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const labelRef = useRef(label);
  labelRef.current = label;
  const renameMutateRef = useRef(rename.mutate);
  renameMutateRef.current = rename.mutate;
  const renamePendingRef = useRef(rename.isPending);
  renamePendingRef.current = rename.isPending;
  const bodyH1 = useMemo(() => (draft !== null ? firstH1(draft) : null), [draft]);
  useEffect(() => {
    if (bodyH1 === null || bodyH1 === lastH1Ref.current) return;
    const t = window.setTimeout(() => {
      lastH1Ref.current = bodyH1;
      if (lockedRef.current) return;
      if (!renamePendingRef.current && bodyH1 !== labelRef.current) renameMutateRef.current(bodyH1);
    }, H1_RENAME_MS);
    return () => window.clearTimeout(t);
  }, [bodyH1]);

  // Backstop for the gap between the last keystroke and the debounce firing: if
  // the page is unloaded or this editor unmounts while still dirty, flush the
  // pending edit with a keepalive request that outlives the page. Refs keep the
  // listener reading the latest values without re-subscribing each keystroke.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    const flush = () => {
      if (dirtyRef.current && draftRef.current !== null) flushNodeText(nodeId, draftRef.current);
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [nodeId]);

  // Shared by the header Save button and the editor's Cmd/Ctrl+S keymap (wired
  // via CodeMirrorEditor's onSave prop, so no textarea keydown handler is needed).
  const onSave = () => {
    if (draft !== null && dirty) runSave(draft);
  };

  // Download menu (Markdown / PDF). `busyDownload` is the KEY of the item
  // rendering (its spinner + the trigger spinner); the menu is kept open while
  // busy so the in-item spinner shows (Base UI closes on select otherwise).
  const [busyDownload, setBusyDownload] = useState<string | null>(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadBase = () => sanitizeFilename(node.data?.label, nodeId);

  // Markdown: generated CLIENT-SIDE from the live `draft` — which `onChange`
  // keeps in lockstep with the on-screen text every keystroke, so the saved
  // bytes equal the editor regardless of autosave state. No backend route for
  // raw note text exists (or is needed).
  const downloadMarkdown = () => {
    const blob = new Blob([draft ?? ""], { type: "text/markdown;charset=utf-8" });
    saveBlobAsFile(blob, `${downloadBase()}.md`);
  };

  // PDF: the server compiles the STORED body (embedding attachment images), so
  // flush the live draft first — await a real save (the pagehide flush is
  // keepalive/fire-and-forget and would race the compile) so the PDF is fresh.
  const downloadNotePdf = async () => {
    if (draft !== null && dirty) await saveAsync(draft);
    const res = await api.request<{ created: boolean; file_url: string; filename: string }>(
      "POST",
      `/api/nodes/${nodeId}/compiled-pdf`,
    );
    await downloadArtifact(res, downloadBase());
  };

  // One runner for both items: mark it busy, run it, route failures through
  // notifyError, then close the menu (kept open until now for the in-item spinner).
  const runDownload = async (key: string, fn: () => void | Promise<void>) => {
    if (busyDownload) return;
    setBusyDownload(key);
    try {
      await fn();
    } catch (e) {
      notifyError(e);
    } finally {
      setBusyDownload(null);
      setDownloadMenuOpen(false);
    }
  };

  if (node.isLoading || draft === null) {
    return (
      <div className={cn("grid h-full place-items-center", !visible && "hidden")}>
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }
  if (node.isError || !node.data) {
    // A 404 self-closes this tab (see the effect above) — render nothing
    // rather than flash a dead-end error the user can't act on.
    if (node.error instanceof GraphMindApiError && node.error.status === 404) {
      return <div className={cn("h-full", !visible && "hidden")} />;
    }
    return (
      <div className={cn("grid h-full place-items-center", !visible && "hidden")}>
        <ErrorState error={node.error} onRetry={() => void node.refetch()} label="note" />
      </div>
    );
  }

  const editor = (
    <CodeMirrorEditor
      ref={editorRef}
      value={draft}
      onChange={setDraft}
      onSave={onSave}
      livePreview={mode === "live"}
      ariaLabel="Markdown source"
      onImageFile={(file) => void insertImageFromFile(file)}
    />
  );

  const preview = (
    // `w-full` (mirroring the editor host's `h-full w-full`) makes the scroll
    // area FILL the flex row — without it the area shrinks to its content's
    // max-content width and left-aligns, so the centered `.md-column` inside
    // lands off to the LEFT and the text jumps horizontally when switching
    // Live↔Preview. Filling the row keeps `.md-column`'s centered measure and
    // gutters identical to the editor's `.cm-content`.
    <ScrollArea className="h-full w-full">
      {/* Same readable column as the editor + title, so switching Live/Source/
          Preview never shifts the measure or the gutters. */}
      <div className="md-column prose-gm py-4">
        <ChunkedPreview
          text={draft}
          onEditRange={(from, to, next) =>
            setDraft((cur) => (cur === null ? cur : cur.slice(0, from) + next + cur.slice(to)))
          }
        />
      </div>
    </ScrollArea>
  );

  return (
    <div className={cn("flex h-full min-h-0 flex-col bg-[#14170f]", !visible && "hidden")}>
      {/* The one thin, borderless title bar (redesign point 2): the note's
          FILENAME, centred + muted + editable. Its big `# H1` lives in the body
          below — title = filename = H1 (point 4), kept in step by `renameNote`
          + the H1→filename reconcile effect above. */}
      <DocTitleBar
        title={node.data.label}
        onRename={renameNote}
        busy={rename.isPending}
        editable={!locked}
        renameHint={locked ? "This file's name is fixed" : "Rename note"}
      />

      {/* Editing toolbar — BORDERLESS now (point 5): no divider, no boxed
          button groups. Just the writing tools (formatting, mode toggle) plus
          download + save, right-aligned so the surface reads as one clean
          column. */}
      <div className="flex shrink-0 items-center justify-end gap-1 px-4 pb-2">
        {mode !== "preview" && (
          <div className="mx-1 flex items-center gap-0.5">
            <FormatButton label="Bold (Ctrl/Cmd+B)" Icon={Bold} onRun={() => runFormat(wrapRange("**"))} />
            <FormatButton label="Italic (Ctrl/Cmd+I)" Icon={Italic} onRun={() => runFormat(wrapRange("*"))} />
            <FormatButton label="Inline code (Ctrl/Cmd+E)" Icon={Code2} onRun={() => runFormat(wrapRange("`"))} />
            <FormatButton label="Highlight (Ctrl/Cmd+Shift+H)" Icon={Highlighter} onRun={() => runFormat(wrapRange("=="))} />
            <FormatButton label="Link (Ctrl/Cmd+K)" Icon={Link2} onRun={() => runFormat(insertLink)} />
            <TableSizePicker onPick={(rows, cols) => runFormat(insertTable(rows, cols))} />
            <FormatButton
              label={insertingImage ? "Uploading image…" : "Insert image (in body)"}
              Icon={insertingImage ? Loader2 : ImageIcon}
              iconClassName={insertingImage ? "animate-spin" : undefined}
              onRun={() => imageInputRef.current?.click()}
            />
          </div>
        )}
        <div className="mr-1 flex items-center gap-0.5">
          <ModeButton current={mode} value="live" onSet={setMode} Icon={PenLine} label="Live preview (compile as you type)" />
          <ModeButton current={mode} value="source" onSet={setMode} Icon={Code} label="Source (raw markdown)" />
          <ModeButton current={mode} value="preview" onSet={setMode} Icon={Eye} label="Preview (read-only)" />
        </div>
        <Dropdown open={downloadMenuOpen} onOpenChange={(o) => setDownloadMenuOpen(o)}>
          <Tip label="Download">
            <DropdownTrigger asChild>
              <button
                type="button"
                aria-label="Download"
                disabled={busyDownload !== null}
                className="mr-1 rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-45"
              >
                {busyDownload !== null ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
              </button>
            </DropdownTrigger>
          </Tip>
          <DropdownContent align="end">
            <DropdownItem
              closeOnClick={false}
              disabled={busyDownload !== null}
              onSelect={() => void runDownload("markdown", downloadMarkdown)}
            >
              {busyDownload === "markdown" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              ) : (
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              Markdown (.md)
            </DropdownItem>
            <DropdownItem
              closeOnClick={false}
              disabled={busyDownload !== null}
              onSelect={() => void runDownload("pdf", downloadNotePdf)}
            >
              {busyDownload === "pdf" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              ) : (
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              PDF
            </DropdownItem>
          </DropdownContent>
        </Dropdown>
        <Tip
          label={
            fullscreen
              ? "Exit fullscreen — Esc"
              : "Fullscreen — the note as a borderless writing sheet (Esc exits)"
          }
        >
          <button
            type="button"
            aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={toggleFullscreen}
            className="mr-1 rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </Tip>
        {/* No Save BUTTON: the body autosaves on a pause, on unmount and on
            pagehide, so a control that only ever did what already happened was
            one more thing in the way. What survives is the quiet status it
            carried — and only while there is something to say. A settled note
            shows nothing at all. Cmd/Ctrl+S still forces a save for anyone with
            the reflex (see `editingBindings`). */}
        <span
          aria-live="polite"
          className="ml-1 min-w-[4.5rem] text-right font-mono text-[11px] text-muted-foreground"
        >
          {saving ? "Saving…" : dirty ? "Unsaved" : ""}
        </span>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = ""; // allow re-picking the same file next time
            if (file) void insertImageFromFile(file);
          }}
        />
      </div>

      {/* Body: a single Live/Source editing surface, or the read-only Preview.
          Live and Source share one CodeMirror instance (the `livePreview` prop
          toggles its decoration layer in place), so switching keeps the caret. */}
      <div className="flex min-h-0 flex-1">{mode === "preview" ? preview : editor}</div>
    </div>
  );
}

function ModeButton({
  current,
  value,
  onSet,
  Icon,
  label,
}: {
  current: Mode;
  value: Mode;
  onSet: (m: Mode) => void;
  Icon: typeof Eye;
  label: string;
}) {
  return (
    <Tip label={label}>
      <button
        onClick={() => onSet(value)}
        aria-label={label}
        aria-pressed={current === value}
        className={cn(
          "rounded p-1.5 transition-colors",
          current === value ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground",
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </button>
    </Tip>
  );
}

/** A formatting-toolbar icon button (bold/italic/code/highlight/link) — the
 * mouse-discoverable counterpart to the CodeMirror keybindings in `cm/keymap`,
 * so someone who doesn't already know Cmd/Ctrl+B has an obvious way to bold
 * text. `onMouseDown` preventDefault keeps focus (and the text selection the
 * command wraps) in the editor instead of the browser shifting it to the
 * button on click. */
function FormatButton({
  label,
  Icon,
  iconClassName,
  onRun,
}: {
  label: string;
  Icon: typeof Bold;
  /** Extra classes on the icon itself, e.g. `"animate-spin"` while a
   *  FormatButton doubles as a busy indicator (see the Insert Image button). */
  iconClassName?: string;
  onRun: () => void;
}) {
  return (
    <Tip label={label}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onRun}
        aria-label={label}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Icon className={cn("h-3.5 w-3.5", iconClassName)} />
      </button>
    </Tip>
  );
}
